import { useState, useEffect } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useAuth0 } from "../hooks/useAuth0";
import { useRole } from "../hooks/useRole";
import api from "../services/api";

const usStates = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY",
];

/**
 * Persistent reminder for a Provider whose account is currently on the
 * Default Practice — either because they signed up without picking one, or
 * because an admin rejected the practice they originally requested (see
 * CognitoService.resolvePracticeApproval). Shows across every page via
 * Layout.jsx, since this isn't something to only surface on one specific
 * screen.
 */
export default function DefaultPracticeBanner() {
  const { isAuthenticated } = useAuth0();
  const { isProvider, loading: roleLoading } = useRole();

  const [isDefault, setIsDefault] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);

  const [practiceOptions, setPracticeOptions] = useState([]);
  const [practiceInputValue, setPracticeInputValue] = useState("");
  const [practiceId, setPracticeId] = useState(null);
  const [practiceAddress, setPracticeAddress] = useState("");
  const [practiceCity, setPracticeCity] = useState("");
  const [practiceState, setPracticeState] = useState("");
  const [practiceZip, setPracticeZip] = useState("");
  const [practicePhone, setPracticePhone] = useState("");

  useEffect(() => {
    if (!isAuthenticated || roleLoading || !isProvider()) return;
    api
      .get("/v1/provider-account/me")
      .then((res) => setIsDefault(!!res.data?.isDefaultPractice))
      .catch(() => setIsDefault(false));
  }, [isAuthenticated, roleLoading, isProvider]);

  useEffect(() => {
    if (!practiceInputValue.trim()) {
      setPracticeOptions([]);
      return;
    }
    const timeout = setTimeout(async () => {
      try {
        const res = await api.get("/v1/organizations/search", {
          params: { q: practiceInputValue },
        });
        setPracticeOptions(res.data || []);
      } catch {
        setPracticeOptions([]);
      }
    }, 350);
    return () => clearTimeout(timeout);
  }, [practiceInputValue]);

  if (!isAuthenticated || roleLoading || !isProvider() || !isDefault) {
    return null;
  }

  const handleSubmit = async () => {
    if (!practiceInputValue.trim()) return;
    setSubmitting(true);
    try {
      const res = await api.post("/v1/provider-account/me/practice", {
        ...(practiceId
          ? { practiceId }
          : {
              practiceName: practiceInputValue,
              practiceAddress,
              practiceCity,
              practiceState,
              practiceZip,
              practicePhone,
            }),
      });
      setSubmitResult(res.data);
      if (!res.data.needsReview) {
        setIsDefault(false);
      }
    } catch (err) {
      setSubmitResult({ message: "Something went wrong — please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Alert
        severity="warning"
        sx={{ borderRadius: 0 }}
        action={
          <Button color="inherit" size="small" onClick={() => setDialogOpen(true)}>
            Pick my practice
          </Button>
        }
      >
        Your account is currently on our Default Practice. Please select or request your
        real practice so your patients show up correctly.
      </Alert>

      <Dialog open={dialogOpen} onClose={() => !submitting && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Select your practice</DialogTitle>
        <DialogContent>
          {submitResult ? (
            <Alert severity={submitResult.needsReview ? "info" : "success"} sx={{ mt: 1 }}>
              {submitResult.message}
            </Alert>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
              <Autocomplete
                freeSolo
                options={practiceOptions}
                getOptionLabel={(option) =>
                  typeof option === "string"
                    ? option
                    : `${option.name}${option.city ? ` — ${option.city}, ${option.state}` : ""}`
                }
                inputValue={practiceInputValue}
                onInputChange={(event, newInputValue) => {
                  setPracticeInputValue(newInputValue);
                  setPracticeId(null);
                }}
                onChange={(event, newValue) => {
                  if (newValue && typeof newValue === "object") {
                    setPracticeId(newValue.id);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label="Practice"
                    helperText="Search for your practice, or type a new one to request it"
                  />
                )}
              />

              {practiceInputValue && !practiceId && (
                <>
                  <TextField
                    fullWidth
                    label="Practice Address (optional)"
                    value={practiceAddress}
                    onChange={(e) => setPracticeAddress(e.target.value)}
                  />
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <TextField
                      fullWidth
                      label="City"
                      value={practiceCity}
                      onChange={(e) => setPracticeCity(e.target.value)}
                    />
                    <FormControl fullWidth>
                      <InputLabel>State</InputLabel>
                      <Select
                        value={practiceState}
                        onChange={(e) => setPracticeState(e.target.value)}
                        label="State"
                      >
                        {usStates.map((s) => (
                          <MenuItem key={s} value={s}>
                            {s}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      fullWidth
                      label="Zip"
                      value={practiceZip}
                      onChange={(e) => setPracticeZip(e.target.value)}
                    />
                  </Box>
                  <TextField
                    fullWidth
                    label="Practice Phone (optional)"
                    value={practicePhone}
                    onChange={(e) => setPracticePhone(e.target.value)}
                  />
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={submitting}>
            {submitResult ? "Close" : "Cancel"}
          </Button>
          {!submitResult && (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting || !practiceInputValue.trim()}
            >
              Submit
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}